import React from "react";
import { useSelector } from "react-redux";
import { Field, reduxForm } from "redux-form";
import Reward from "react-rewards";

import { auth, database } from "../firebase.config";

import PropTypes from "prop-types";
import { makeStyles, createStyles, withStyles } from "@material-ui/core/styles";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

const CssTextField = withStyles({
  root: {
    "& label": {
      color: "white",
    },
    "& input": {
      color: "white",
    },
    "& label.Mui-focused": {
      color: "white",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "white",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white",
      },
      "&:hover fieldset": {
        borderColor: "white",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white",
      },
    },
  },
})(TextField);

const useStyles = makeStyles((theme) =>
  createStyles({
    paper: {
      marginTop: 18,
      marginBottom: 18,
      paddingBottom: 12,
      paddingRight: 12,
      paddingLeft: 12,
      background: theme.palette.primary.light,
    },
    readingListTitle: {
      paddingTop: 12,
      color: "white",
      background: theme.palette.primary.light,
    },
    readingListSubtitle: {
      color: "white",
      background: theme.palette.primary.light,
    },
  })
);

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function LinearProgressWithLabel(props) {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box minWidth={35}>
        <Typography style={{ color: "white" }} variant="body2">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

LinearProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate and buffer variants.
   * Value between 0 and 100.
   */
  value: PropTypes.number.isRequired,
};

const renderCssTextField = ({
  input,
  label,
  meta: { touched, error },
  ...custom
}) => {
  return (
    <CssTextField
      id="progressField"
      label="What page are you at?"
      variant="filled"
      color="secondary"
      fullWidth={true}
      type="number"
      style={{ color: "white", marginTop: 8 }}
      {...input}
      {...custom}
    />
  );
};

function ProgressTracker(props) {
  const classes = useStyles();
  let reward = null;

  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const [isUpdateButtonDisabled, setUpdateButtonDisabled] = React.useState(
    false
  );

  const [isError, setError] = React.useState(false);

  const progress = useSelector((state) => state.form.progressTrackerForm);
  const numberOfPages = useSelector((state) =>
    state.bookDetails.volumeInfo ? state.bookDetails.volumeInfo.pageCount : 0
  );
  const userReadPages = useSelector((state) =>
    state.readingList[props.bookId].progress
      ? state.readingList[props.bookId].progress
      : 0
  );

  const validateValue = (userNumber, numberOfPages) => {
    if (userNumber < 0 || userNumber > numberOfPages) {
      return false;
    } else {
      return true;
    }
  };

  const handleProgressTrackerSubmit = async (e) => {
    e.preventDefault();

    handleClose();
    let isValid = false;
    if (progress.values) {
      isValid = validateValue(progress.values.progressTracker, numberOfPages);
    }

    if (isValid) {
      setUpdateButtonDisabled(true);
      const bookId = props.bookId;
      const uid = auth.currentUser.uid;

      await database
        .collection("users")
        .doc(uid)
        .collection("ReadingList")
        .doc(bookId)
        .update({ progress: parseInt(progress.values.progressTracker) });

      props.change("progressTracker", "");
      setError(false);
      setUpdateButtonDisabled(false);
      handleClick();
    } else {
      reward.punishMe();
      setError(true);
    }
  };

  return (
    <>
      <Paper elevation={3} className={classes.paper}>
        <Typography variant="h5" className={classes.readingListTitle}>
          Track Your Progress
        </Typography>

        <Typography
          variant="subtitle1"
          gutterBottom
          className={classes.readingListSubtitle}
        >
          You have completed {userReadPages} pages out of {numberOfPages}
        </Typography>
        <LinearProgressWithLabel
          value={(userReadPages / numberOfPages) * 100}
          color="secondary"
        />

        <form onSubmit={handleProgressTrackerSubmit}>
          <Field
            name="progressTracker"
            disabled={isUpdateButtonDisabled}
            component={renderCssTextField}
            error={isError}
          />
          <Reward
            ref={(ref) => {
              reward = ref;
            }}
            type="confetti"
          >
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isUpdateButtonDisabled}
              style={{ marginTop: 8 }}
            >
              Update Progress
            </Button>
          </Reward>
        </form>
      </Paper>
      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Progress Updated Successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default reduxForm({
  form: "progressTrackerForm",
})(ProgressTracker);
